import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  clearPendingPromise,
  getCachedResult,
  isPendingPromise,
  pendingPromise,
  setCachedResult,
  setPendingPromise,
} from '../Http/cache';

interface Response<T> {
  loading: boolean;
  data?: T;
  error: boolean;
}

const useFetch = <T>(
  path: RequestInfo,
  options?: RequestInit,
  skip?: boolean,
  promiseCache?: boolean
): Response<T> => {
  const [data, setData] = useState<T | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const doFetch = useCallback(async () => {
    const key = path.toString();
    const cached = getCachedResult<T>(key);
    setError(false);
    if (cached) {
      setData(cached);
      return;
    }
    setLoading(true);
    try {
      const reusePromise = promiseCache && isPendingPromise(key);
      const request = reusePromise ? pendingPromise[key] : fetch(path, options);
      if (promiseCache) {
        setPendingPromise(key, request);
      }

      const result = await request;
      if (result.ok) {
        const json = await result.clone().json();
        setCachedResult(key, json);
        setData(json);
      }
    } catch (_e) {
      setError(true);
    } finally {
      setLoading(false);
      clearPendingPromise(key);
    }
    return;
  }, [path, options]);

  useEffect(() => {
    if (!skip) {
      doFetch();
    }
  }, [path, skip]);

  const _data = useMemo(
    () => (loading ? data : getCachedResult<T>(path.toString())),
    [path, data, loading]
  );

  return {
    loading,
    data: _data,
    error,
  };
};

export default useFetch;
